import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, Award, Users, Clock } from 'lucide-react';
import AnimatedNumber from '../components/AnimatedNumber';

export default function About() {
  return (
    <>
      <Helmet>
        <title>Om MJ Detailing | Passion for Professionel Bilpleje</title>
        <meta name="description" content="Læs historien bag MJ Detailing i Billund. Vi leverer kompromisløs bilpleje med fokus på kvalitet, punktlighed &amp; personlig service. Se vores resultater her!" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-zinc-500 font-bold tracking-widest uppercase text-sm mb-4 block">MJ Detailing</span>
            <h1 className="text-5xl md:text-7xl font-black tracking-normal mb-8 leading-tight">
              PASSION FOR <br />
              <span className="bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">PERFEKTION</span>
            </h1>
            <p className="text-xl text-zinc-400 leading-relaxed">
              Hos MJ-Detailing leverer vi professionel bilpleje med fokus på kvalitet, glans og langtidsholdbar beskyttelse. Vi tilbyder grundig rengøring, effektiv polering og keramisk coating, der giver lakken en dyb glans og stærk beskyttelse mod snavs og slid.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="relative aspect-[4/5] md:aspect-[16/10] lg:aspect-[4/5] rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/f263d9ea-9b8f-459a-a9a0-5c207222b9e6.jpg"
                alt="Detailing Process"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-8">
              <h2 className="text-4xl font-black tracking-normal uppercase">Vores Filosofi</h2>
              <p className="text-zinc-400 leading-relaxed text-lg">
                Vi tror på, at hver bil fortjener individuel opmærksomhed. Der findes ingen standardløsninger hos os – hver lak og hvert interiør kræver sin egen specifikke tilgang og de rette produkter.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-4">
                {[
                  { icon: <Award className="text-white" />, title: 'Kvalitetsbevidst', desc: 'Vi går aldrig på kompromis med materialer eller tid.' },
                  { icon: <Users className="text-white" />, title: 'Personlig Service', desc: 'Du får altid en ærlig vurdering og personlig rådgivning.' },
                  { icon: <Clock className="text-white" />, title: 'Punktlighed', desc: 'Vi overholder vores aftaler og leverer til tiden.' },
                  { icon: <CheckCircle2 className="text-white" />, title: 'Resultatorienteret', desc: 'Vi stopper ikke, før resultatet er perfekt.' }
                ].map((item, index) => (
                  <div key={index} className="space-y-3">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10">
                      {item.icon}
                    </div>
                    <h4 className="font-bold uppercase tracking-normal">{item.title}</h4>
                    <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats/Values */}
      <section className="py-12 lg:py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
            {[
              { label: 'Biler Behandlet', value: 500, suffix: '+' },
              { label: 'Glade Kunder', value: 100, suffix: '%' },
              { label: 'Års Erfaring', value: 5, suffix: '+' },
              { label: 'Certificeringer', value: 3, suffix: '' }
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-4xl md:text-6xl font-black tracking-normal mb-2">
                  <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-zinc-500 text-xs uppercase tracking-widest font-bold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-32 bg-black relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-zinc-900/20 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto bg-zinc-950 border border-white/5 rounded-[2.5rem] p-8 md:p-20 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-normal mb-8 leading-tight">
                SKAL VI TAGE HÅND OM <br /> <span className="bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">DIN BIL?</span>
              </h2>
              <p className="text-zinc-400 mb-10 max-w-xl mx-auto text-lg">
                Uanset om det er en dagligdagsbil eller en samlerbil, behandler vi den med samme respekt og grundighed.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-3 bg-white text-black px-8 py-3.5 rounded-full font-bold text-base uppercase tracking-widest hover:bg-zinc-200 transition-all"
              >
                Kontakt os i dag
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
